<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import { formatAbsoluteDate, formatDate } from '../composables/useFormattedDate'
import { useNow } from '../composables/useNow'
import { entityTypeBadge, ENTITY_TYPE_BADGE_BASE_CLASSES } from '../composables/entityTypeBadge'

// One card for a wiki page, shared across every browse surface
// (HomeLanding's recent grid, CategoryView, the memory view) so the
// card shell lives in one place instead of three divergent inline
// `<li>` blocks. Renders an `<li>` so the parent `<ul>`
// owns the grid-vs-column layout; the card just fills its cell.
//
// Takes primitive fields rather than a `WikiPage`/`MemoryEntry` object
// so both row types map onto it without coupling. Optional bits render
// only when supplied:
//   - `tag`        — small top-right label (category for wiki pages, the
//                    entry type for memory entries)
//   - `entityType` — entities/ classification (person/org/product/
//                    project), rendered as a color-coded badge via
//                    entityTypeBadge — null for every other page
//   - `description`— one-line blurb under the title (memory entries)
//   - `scopeBadge` — coloured scope pill (memory: Global = purple,
//                    project = blue)
//   - `hidePath`   — suppress the monospace path line (memory cards,
//                    where the path is redundant with the title)
//   - `createdAt`  — when it lands on a different day than `updatedAt`,
//                    an "Added …" line surfaces the entry's true age
//                    (memory hygiene — an old note touched recently by
//                    Lint still reads as "Updated today" otherwise)
//   - `selectable` — render as a button that emits `select` instead of a
//                    navigating link (memory cards open a drawer in place
//                    rather than dumping to the raw markdown page)
const props = withDefaults(
  defineProps<{
    path: string
    title?: string | null
    updatedAt?: string | null
    createdAt?: string | null
    tag?: string | null
    entityType?: string | null
    description?: string | null
    scopeBadge?: { label: string; isGlobal: boolean } | null
    hidePath?: boolean
    selectable?: boolean
  }>(),
  {
    title: null,
    updatedAt: null,
    createdAt: null,
    tag: null,
    entityType: null,
    description: null,
    scopeBadge: null,
    hidePath: false,
    selectable: false,
  },
)

const emit = defineEmits<{ (e: 'select', path: string): void }>()

const now = useNow()
const entityBadge = computed(() => entityTypeBadge(props.entityType))

// Only worth showing "Added" when it adds information — i.e. the entry
// was created on a different calendar day than it was last updated.
// ISO timestamps compare by their `YYYY-MM-DD` prefix.
const showCreated = computed(
  () => Boolean(props.createdAt) && props.createdAt?.slice(0, 10) !== props.updatedAt?.slice(0, 10),
)

// In `selectable` mode the card is a button that emits; otherwise the
// default `@click` is a no-op and the RouterLink navigates as normal.
function onActivate(): void {
  if (props.selectable) emit('select', props.path)
}
</script>

<template>
  <li class="border border-white/10 rounded-lg hover:border-white/30 hover:bg-white/[0.02] transition-colors">
    <component
      :is="selectable ? 'button' : RouterLink"
      :to="selectable ? undefined : `/p/${path}`"
      :type="selectable ? 'button' : undefined"
      class="block w-full text-left p-3 no-underline h-full flex flex-col gap-2"
      data-testid="wiki-page-card"
      @click="onActivate"
    >
      <div class="flex items-start justify-between gap-2">
        <span class="text-white text-sm font-medium leading-snug truncate">
          {{ title || path }}
        </span>
        <span v-if="tag" class="shrink-0 text-[10px] uppercase tracking-widest text-white/40">{{ tag }}</span>
      </div>

      <span
        v-if="scopeBadge"
        class="inline-flex items-center gap-1.5 self-start text-[11px] text-white/60"
        data-testid="wiki-page-card-scope"
      >
        <span
          class="inline-block w-2 h-2 rounded-sm"
          :class="scopeBadge.isGlobal ? 'bg-purple-500' : 'bg-blue-500'"
          aria-hidden="true"
        />
        {{ scopeBadge.label }}
      </span>

      <span
        v-if="entityBadge"
        class="self-start"
        :class="[ENTITY_TYPE_BADGE_BASE_CLASSES, entityBadge.classes]"
        data-testid="wiki-page-card-entity-type"
        >{{ entityBadge.label }}</span
      >

      <p v-if="description" class="text-xs text-white/50 m-0">{{ description }}</p>

      <span v-if="!hidePath" class="text-xs text-white/40 font-mono truncate">{{ path }}</span>

      <div class="mt-auto flex flex-col">
        <time
          v-if="showCreated && createdAt"
          :datetime="createdAt"
          :title="formatAbsoluteDate(createdAt)"
          class="text-[11px] text-white/30"
          >Added {{ formatDate(createdAt, now) }}</time
        >
        <time
          v-if="updatedAt"
          :datetime="updatedAt"
          :title="formatAbsoluteDate(updatedAt)"
          class="text-[11px] text-white/40"
          >Updated {{ formatDate(updatedAt, now) }}</time
        >
      </div>
    </component>
  </li>
</template>
